import { useEffect, useState } from "react";
import { Boxes } from "lucide-react";
import { StockBadge, STOCK_STATES } from "@/components/StockBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = ["ready", "low", "made_to_order"];

export default function StockManager() {
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then(setProducts)
      .catch((e) => console.debug("Stock manager load failed:", e));
  }, []);

  const setStatus = async (id, status) => {
    setSaving(id);
    setError("");
    try {
      const res = await fetch(`${API}/admin/stock/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("save failed");
      setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, stock_status: status } : p)));
    } catch (err) {
      console.debug("Stock update failed:", err);
      setError("Could not save that change. Please try again.");
    } finally {
      setSaving("");
    }
  };

  return (
    <section
      className="border border-[var(--mp-line)] rounded-sm p-6 mb-10"
      style={{ backgroundColor: "var(--mp-card)" }}
      data-testid="admin-stock-manager"
    >
      <div className="flex items-center gap-3 mb-1">
        <Boxes className="w-4 h-4" style={{ color: "var(--mp-copper)" }} />
        <h2
          className="text-xl font-medium"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
        >
          Stock Status
        </h2>
      </div>
      <p className="text-xs mb-6" style={{ color: "var(--mp-muted)" }}>
        What buyers see on the product cards, comparison table and shade pages.
      </p>

      {error && (
        <p className="text-xs mb-4" style={{ color: "#DC2626" }} data-testid="stock-manager-error">
          {error}
        </p>
      )}

      <div className="space-y-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="flex flex-wrap items-center justify-between gap-3 py-2 border-b border-[var(--mp-line)] last:border-0"
            data-testid={`stock-row-${p.id}`}
          >
            <div className="flex items-center gap-3 min-w-[220px]">
              <span
                className="text-sm font-medium"
                style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
              >
                {p.name}
              </span>
              <StockBadge status={p.stock_status || "ready"} />
            </div>
            <div className="flex gap-2">
              {ORDER.map((key) => {
                const active = (p.stock_status || "ready") === key;
                return (
                  <button
                    key={key}
                    onClick={() => setStatus(p.id, key)}
                    disabled={saving === p.id}
                    className="text-[11px] font-semibold px-3 py-1.5 rounded-sm border transition-colors disabled:opacity-50"
                    style={{
                      borderColor: active ? "var(--mp-solid)" : "var(--mp-line)",
                      backgroundColor: active ? "var(--mp-solid)" : "transparent",
                      color: active ? "var(--mp-on-solid)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid={`stock-set-${p.id}-${key}`}
                  >
                    {STOCK_STATES[key].label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
